import { useEffect, useRef, useCallback, useState } from "react";
import { type StoreApi } from "zustand";
import { useVerificationStoreApi } from "../state/store-context";
import { selectBatchLabel } from "../state/store";
import type { VerificationState } from "../state/types";

/**
 * BroadcastChannel-based panel detachment.
 *
 * The main window sends store state snapshots to the detached window.
 * The detached window sends field edits back.
 *
 * Channel messages:
 *   main → detached:  { type: "state", payload: { fields, groups, ... } }
 *   detached → main:  { type: "field_edit", fieldId, value }
 *   detached → main:  { type: "advance_field", direction }
 *   detached → main:  { type: "set_active", index }
 *   detached → main:  { type: "toggle_focus" }
 *   detached → main:  { type: "request_state" }       — R-137 fix
 *   detached → main:  { type: "panel_closed" }
 *   main → detached:  { type: "close" }
 *
 * R-137 (2026-05-23): the original design pushed the initial snapshot on the
 * popup window's `load` event, but the child's BroadcastChannel subscription
 * only attaches after React effects run — the push regularly arrived before
 * the listener and was lost. Symptom: detached window stuck on the
 * "Waiting…" view, which read as a black window. Fix: the child now sends
 * `request_state` on mount; the parent responds with a fresh snapshot.
 */

const CHANNEL_NAME = "capture-panel";

interface PanelState {
  fields: VerificationState["fields"];
  groups: VerificationState["groups"];
  activeFieldIndex: number;
  focusMode: boolean;
  lineItems: VerificationState["lineItems"];
  documentId: string | null;
  /** E-050 — carries the preferred batch label (customDisplayLabel ??
   *  displayId ?? name), not the raw free-text name. Wire field name kept as
   *  `batchName` so a mixed-version main/detached window pair stays legible. */
  batchName: string;
  filename: string;
  snippetVisible: boolean;
}

function getSnapshot(store: StoreApi<VerificationState>): PanelState {
  const s = store.getState();
  return {
    fields: s.fields,
    groups: s.groups,
    activeFieldIndex: s.activeFieldIndex,
    focusMode: s.focusMode,
    lineItems: s.lineItems,
    documentId: s.documentId,
    batchName: selectBatchLabel(s),
    filename: s.filename,
    snippetVisible: s.snippetVisible,
  };
}

export function useDetachablePanel() {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const windowRef = useRef<Window | null>(null);
  const [isDetached, setIsDetached] = useState(false);

  // SP3 §4 — bind to the enclosing pane's store (default instance in the
  // standalone VS, where no provider is mounted). Held in a ref so the
  // stable-deps effects below read the current api without re-subscribing.
  const storeApi = useVerificationStoreApi();
  const storeRef = useRef(storeApi);
  storeRef.current = storeApi;

  // Listen for messages from detached window
  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const msg = event.data;
      if (!msg || typeof msg !== "object") return;

      switch (msg.type) {
        case "field_edit":
          storeRef.current.getState().setFieldValue(msg.fieldId, msg.value);
          break;
        case "advance_field":
          storeRef.current.getState().advanceField(msg.direction);
          break;
        case "set_active":
          storeRef.current.getState().setActiveField(msg.index);
          break;
        case "toggle_focus":
          storeRef.current.getState().toggleFocusMode();
          break;
        case "request_state":
          // R-137: child asks for a fresh snapshot on mount because the
          // load-event push race regularly lost the initial state.
          channel.postMessage({ type: "state", payload: getSnapshot(storeRef.current) });
          break;
        case "panel_closed":
          setIsDetached(false);
          windowRef.current = null;
          break;
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, []);

  // Push state updates to detached window
  useEffect(() => {
    if (!isDetached) return;

    const unsub = storeRef.current.subscribe(() => {
      channelRef.current?.postMessage({
        type: "state",
        payload: getSnapshot(storeRef.current),
      });
    });

    return unsub;
  }, [isDetached]);

  const detach = useCallback(() => {
    const w = window.open(
      "/panel-detached",
      "capture-field-panel",
      "width=400,height=800,menubar=no,toolbar=no,location=no,status=no",
    );

    if (!w) return; // popup blocked

    windowRef.current = w;
    setIsDetached(true);

    // Send initial state once the window loads
    w.addEventListener("load", () => {
      channelRef.current?.postMessage({
        type: "state",
        payload: getSnapshot(storeRef.current),
      });
    });

    // Detect if the user closes the popup
    const checkClosed = setInterval(() => {
      if (w.closed) {
        clearInterval(checkClosed);
        setIsDetached(false);
        windowRef.current = null;
      }
    }, 1000);
  }, []);

  const reattach = useCallback(() => {
    channelRef.current?.postMessage({ type: "close" });
    windowRef.current?.close();
    windowRef.current = null;
    setIsDetached(false);
  }, []);

  return { isDetached, detach, reattach };
}
